/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, ClipboardList, Trash2,
  Beaker, MessageSquarePlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const ISSUE_TYPE_LABEL = {
  intolerance: 'Intolerance',
  taste_or_smell: 'Taste / smell',
  timing_difficulty: 'Timing difficulty',
  complexity: 'Complexity',
  doubt_or_trust: 'Doubt / trust',
  missed_due_to_schedule: 'Missed due to schedule',
  delivery_or_supply: 'Delivery / supply',
  other: 'Other',
};

const SOURCE_LABEL = {
  support_ticket: 'Support ticket',
  daily_execution: 'Daily execution',
  pause_reason: 'Pause reason',
  skip_reason: 'Skip reason',
  counselor_manual: 'Counselor (manual)',
  admin_manual: 'Admin (manual)',
};

export default function FormulationFeedback() {
  const { subscriberId, phaseId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = user?.role === 'admin' || user?.role === 'counselor';

  const [data, setData] = useState({ items: [], total: 0, by_source: {}, by_issue_type: {} });
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [filter, setFilter] = useState({ source: 'all', issue_type: 'all' });

  // Manual add form
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState('complexity');
  const [addNote, setAddNote] = useState('');
  const [adding, setAdding] = useState(false);

  const [ingesting, setIngesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const params = new URLSearchParams();
      if (filter.source !== 'all') params.append('source', filter.source);
      if (filter.issue_type !== 'all') params.append('issue_type', filter.issue_type);
      const r = await axios.get(
        `${API}/formulation/feedback/${subscriberId}/${phaseId}?${params.toString()}`,
      );
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load feedback');
    } finally {
      setLoading(false);
    }
  }, [subscriberId, phaseId, filter]);
  useEffect(() => { load(); }, [load]);

  const submitManual = async () => {
    if (!addNote.trim()) {
      toast.error('Note is required');
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API}/formulation/feedback/${subscriberId}/${phaseId}`,
        { issue_type: addType, note: addNote.trim() },
      );
      toast.success('Feedback recorded');
      setAddOpen(false);
      setAddNote('');
      setAddType('complexity');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save feedback');
    } finally {
      setAdding(false);
    }
  };

  const ingest = async () => {
    setIngesting(true);
    try {
      const r = await axios.post(
        `${API}/formulation/feedback/${subscriberId}/${phaseId}/ingest`,
      );
      const { inserted, skipped_existing } = r.data;
      toast.success(`Ingested ${inserted} new (${skipped_existing} duplicates skipped)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ingest failed');
    } finally {
      setIngesting(false);
    }
  };

  const removeRow = async (id) => {
    try {
      await axios.delete(`${API}/formulation/feedback/${id}`);
      toast.success('Feedback removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (!user) return null;

  return (
    <AdminLayout>
      <div
        data-testid="formulation-feedback-page"
        className="px-6 py-6 max-w-5xl mx-auto"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <ClipboardList className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Phase Feedback Log</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">F4</Badge>
          </div>
          <p className="text-sm text-white/50">
            Operational feedback signals captured from skips, pauses, support tickets, and counselor entries.
          </p>
          <p className="text-xs text-white/40 mt-1">
            <span className="font-mono">subscriber:</span> {subscriberId}
            {' · '}
            <span className="font-mono">phase:</span> {phaseId}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {canWrite && (
            <Button
              data-testid="feedback-add-btn"
              onClick={() => setAddOpen(!addOpen)}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
            >
              <MessageSquarePlus className="w-3.5 h-3.5 mr-1" /> Add feedback
            </Button>
          )}
          {isAdmin && (
            <Button
              data-testid="feedback-auto-ingest-btn"
              variant="outline"
              onClick={ingest}
              disabled={ingesting}
              className="border-white/15 text-white/80 h-9 text-xs"
            >
              {ingesting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Auto-ingest now
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-white/15 text-white/70 h-9 text-xs ml-auto"
            data-testid="feedback-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {addOpen && canWrite && (
          <div
            data-testid="feedback-add-form"
            className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 mb-3 space-y-2"
          >
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-white/60">Issue type</Label>
                <Select value={addType} onValueChange={setAddType}>
                  <SelectTrigger
                    data-testid="feedback-add-issue-type"
                    className="bg-slate-950/40 border-white/10 text-sm h-9"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {Object.entries(ISSUE_TYPE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`feedback-add-issue-type-${k}`}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-white/60">Note</Label>
              <Textarea
                data-testid="feedback-add-note-input"
                rows={3}
                maxLength={1000}
                value={addNote}
                onChange={(e) => setAddNote(e.target.value)}
                placeholder="Describe the feedback…"
                className="bg-slate-950/40 border-white/10 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddOpen(false)}
                      className="border-white/15 text-white/70 h-8 text-xs">
                Cancel
              </Button>
              <Button
                data-testid="feedback-add-submit"
                onClick={submitManual}
                disabled={adding}
                className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-8 text-xs"
              >
                {adding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Select value={filter.source} onValueChange={(v) => setFilter((f) => ({ ...f, source: v }))}>
            <SelectTrigger
              data-testid="feedback-filter-source"
              className="w-44 bg-slate-950/40 border-white/10 text-xs h-8"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all">All sources</SelectItem>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.issue_type} onValueChange={(v) => setFilter((f) => ({ ...f, issue_type: v }))}>
            <SelectTrigger
              data-testid="feedback-filter-issue-type"
              className="w-44 bg-slate-950/40 border-white/10 text-xs h-8"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all">All issue types</SelectItem>
              {Object.entries(ISSUE_TYPE_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rollups */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-3 flex flex-wrap gap-3 text-xs">
          <span><span className="text-white/40">Total:</span> <span className="text-white">{data.total}</span></span>
          {Object.entries(data.by_issue_type || {}).map(([k, v]) => (
            <span key={k}>
              <span className="text-white/40">{ISSUE_TYPE_LABEL[k] || k}:</span>{' '}
              <span className="text-white/80">{v}</span>
            </span>
          ))}
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
               data-testid="feedback-page-error">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
          </div>
        )}

        {!loading && !pageError && data.items.length === 0 && (
          <div
            data-testid="feedback-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-8 text-center"
          >
            <Beaker className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <div className="text-sm text-white/70">No feedback captured yet for this phase.</div>
          </div>
        )}

        {!loading && data.items.length > 0 && (
          <div
            data-testid="feedback-list"
            className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden"
          >
            <table className="w-full text-xs">
              <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Captured</th>
                  <th className="text-left px-3 py-2 font-medium">Source</th>
                  <th className="text-left px-3 py-2 font-medium">Issue type</th>
                  <th className="text-left px-3 py-2 font-medium">Note</th>
                  <th className="text-right px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`feedback-row-${row.id}`}
                    className="border-b border-white/5"
                  >
                    <td className="px-3 py-2 text-white/60 whitespace-nowrap">
                      {row.captured_at ? new Date(row.captured_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`feedback-row-source-${row.id}`}
                        className="px-2 py-0.5 rounded border bg-cyan-500/10 text-cyan-200 border-cyan-500/30 text-[10px]"
                      >
                        {SOURCE_LABEL[row.source] || row.source}
                      </span>
                      {row.is_auto_ingested && (
                        <span
                          data-testid={`feedback-row-auto-badge-${row.id}`}
                          className="ml-1 px-1.5 py-0.5 rounded bg-white/5 text-white/40 text-[9px] uppercase"
                        >
                          auto
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-white/80">
                      {ISSUE_TYPE_LABEL[row.issue_type] || row.issue_type}
                    </td>
                    <td className="px-3 py-2 text-white/70 max-w-md">
                      <div className="line-clamp-3 whitespace-pre-wrap">{row.note || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && !row.is_auto_ingested && (
                        <Button
                          data-testid={`feedback-delete-btn-${row.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => removeRow(row.id)}
                          className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10 h-7 text-[10px] px-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
